import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/feature/Navbar';
import Footer from '../../../../components/feature/Footer';

const SentinelMaliyetOptimizasyonuPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#0078D4] to-[#005A9E] overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
            }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/sentinel"
              className="inline-flex items-center space-x-2 text-white/80 hover:text-white text-sm font-medium mb-8 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              <span>Sentinel Sayfasına Dön</span>
            </Link>

            <div className="flex items-center space-x-3 mb-6">
              <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-md text-white text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                Maliyet Optimizasyonu
              </span>
              <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-md text-white text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                Microsoft Sentinel
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
              Sentinel Maliyet Optimizasyonu: Log Yönetimi ve Bütçe Kontrolü
            </h1>

            <div className="flex items-center space-x-6 text-white/70 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-line text-base"></i>
                </div>
                <span>22 Kasım 2025</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-time-line text-base"></i>
                </div>
                <span>Makale</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-user-line text-base"></i>
                </div>
                <span>Özgür Çebi</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Makale İçeriği */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-8">
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <div className="text-gray-700 leading-relaxed space-y-6">
              <p className="text-lg">
                Modern siber güvenlik operasyonlarında Microsoft Sentinel, otomasyon ve analiz yetenekleriyle benzersiz bir güç sunuyor. Ancak altyapı "Kullandıkça Öde" (Pay-As-You-Go) modeliyle çalıştığında, Log Analytics Workspace'e (LAW) akan kontrolsüz veri, ay sonunda bütçe sınırlarını zorlayabiliyor.
              </p>

              <p>
                Güvenlik operasyonlarında sıkça karşılaştığımız temel bir yanılgı var: Her logun eşit değerde olduğu düşüncesi. Sentinel pahalı bir çözüm değildir; faturaları şişiren ana etken, optimize edilmemiş veri mimarisi ve yanlış yapılandırılmış saklama (retention) stratejileridir.
              </p>

              <p>
                Bugün, Sentinel fiyatlandırma modelinin derinliklerine inerek; Commitment Tier, Basic Logs, Archive Katmanı ve oyunun kurallarını değiştiren Data Lake (ADX) Entegrasyonu ile operasyonel körlük yaşamadan bütçenizi nasıl %40'a kadar düşürebileceğinizi inceleyeceğiz.
              </p>

              {/* Görsel Önerisi */}
              <div className="my-10">
                <img
                  src="https://public.readdy.ai/ai/img_res/9e057e16078fdc9bd8c2b5958330b965.jpg"
                  alt="Bütçe Koruma İllüstrasyonu"
                  className="w-full h-auto rounded-2xl shadow-lg object-top"
                />
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center">
                <span className="w-10 h-10 flex items-center justify-center bg-[#0078D4] text-white rounded-lg mr-4 text-xl">1</span>
                Faturanın Anatomisi: Ingestion ve Retention
              </h2>

              <p>
                Sentinel maliyet optimizasyonuna başlamadan önce faturayı oluşturan iki ana bileşeni anlamak şarttır:
              </p>

              <div className="grid grid-cols-2 gap-6 my-8">
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#0078D4]/10 rounded-lg mb-4">
                    <i className="ri-download-cloud-line text-2xl text-[#0078D4]"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Data Ingestion (Veri Alımı)</h3>
                  <p className="text-sm text-gray-600">Log Analytics Workspace'e GB başına yazılan veri.</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#0078D4]/10 rounded-lg mb-4">
                    <i className="ri-database-2-line text-2xl text-[#0078D4]"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Data Retention (Veri Saklama)</h3>
                  <p className="text-sm text-gray-600">Verinin sistemde ne kadar süre "sıcak" (sorgulanabilir) tutulduğu.</p>
                </div>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg my-8">
                <p className="text-base font-medium text-gray-800">
                  <strong>Yaygın Mimari Hata:</strong> Her veriyi varsayılan Analytics (Analitik) tablolara yazmak ve 90 günden uzun süre operasyonel alanda tutmak, maliyetleri katlayarak artıran en yaygın mimari hatadır.
                </p>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center">
                <span className="w-10 h-10 flex items-center justify-center bg-[#0078D4] text-white rounded-lg mr-4 text-xl">2</span>
                İlk Savunma Hattı: Capacity Reservation (Commitment Tier)
              </h2>

              <p>
                Günlük log hacminiz 100 GB bandını aşıyorsa, standart "Pay-As-You-Go" modelinde kalmak finansal bir israftır. Microsoft, belirli bir günlük veri alımını taahhüt etmeniz durumunda (Capacity Reservation) %50'ye varan birim maliyet indirimleri sağlar.
              </p>

              <div className="bg-[#F0F6FF] border border-[#0078D4]/30 rounded-xl p-6 my-8">
                <h4 className="font-bold text-gray-900 mb-4 text-lg">Stratejik Yaklaşım:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#0078D4] text-xl mr-3 mt-1"></i>
                    <span>Sentinel kullanım metriklerinizi (Usage report) analiz ederek son 31 günlük ortalamanızı çıkarın.</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#0078D4] text-xl mr-3 mt-1"></i>
                    <span>Veri hacminize en uygun (100GB, 200GB, 500GB vb.) Capacity Reservation katmanını seçin.</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#0078D4] text-xl mr-3 mt-1"></i>
                    <span>Bu katmanlar 31 günlük periyotlarla değiştirilebilir. Trafik hacminin artacağı dönemleri öngörüp bir üst katmana geçmek ve sonrasında düşürmek, dinamik bir maliyet yönetimi sağlar.</span>
                  </li>
                </ul>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center">
                <span className="w-10 h-10 flex items-center justify-center bg-[#0078D4] text-white rounded-lg mr-4 text-xl">3</span>
                Veri Sınıflandırma Sanatı: Analytics vs. Data Lake Tier
              </h2>

              <p>
                Data Collection Rules (DCR) kullanarak veriyi içeri alırken sınıflandırmak, maliyet optimizasyonunun kalbidir. Her veri, Sentinel'in gelişmiş korelasyon motoruna ihtiyaç duymaz.
              </p>

              <div className="grid grid-cols-1 gap-6 my-8">
                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center text-lg">
                    <i className="ri-star-fill text-green-600 mr-2 text-xl"></i>
                    Analytics Logs (Tam Güç)
                  </h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p><strong>Kullanım:</strong> Microsoft Defender, Entra ID logları, siber tehdit istihbaratı gibi yüksek değerli güvenlik olayları.</p>
                    <p><strong>Yetenekler:</strong> Tam KQL (Kusto Query Language) desteği, Machine Learning algoritmaları ve UEBA (User and Entity Behavior Analytics) entegrasyonu.</p>
                    <p><strong>Maliyet:</strong> En yüksek katman.</p>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center text-lg">
                    <i className="ri-database-line text-blue-600 mr-2 text-xl"></i>
                    Data Lake Tier (Yeni Nesil Depolama)
                  </h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p><strong>Kullanım:</strong> VPC Flow logları, Firewall "Allow" trafikleri, NetFlow gibi hacimli ama düşük güvenlik değerli veriler.</p>
                    <p><strong>Yetenekler:</strong> Eski Auxiliary Logs'un evrimleşmiş hali olan Sentinel Data Lake, 12 yıla kadar saklama imkanı sunar ve tam KQL sorgu desteği sağlar.</p>
                    <p><strong>Kazanç:</strong> Log Analytics maliyetini dramatik ölçüde düşürür. Gürültülü veriyi bu katmana çekmek, faturayı anında hafifletir.</p>
                    <p><strong>Önemli:</strong> Yeni projelerde Data Lake Tier tercih edilmelidir.</p>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center text-lg">
                    <i className="ri-alert-line text-amber-600 mr-2 text-xl"></i>
                    Basic Logs (Legacy - Eski Kalan)
                  </h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p><strong>Durum:</strong> Basic Logs artık "legacy" (eski) kalmaya başladı ve yeni projelerde kullanılması önerilmemektedir.</p>
                    <p><strong>Sınırlamalar:</strong> 8 günlük kısa saklama süresi, sınırlı KQL sorgu yeteneği (join veya summarize operatörleri kullanılamaz).</p>
                    <p><strong>Öneri:</strong> Mevcut Basic Logs kullanımlarınızı Data Lake Tier'a geçirmeyi planlayın.</p>
                  </div>
                </div>
              </div>

              {/* Görsel Önerisi - Veri Sınıflandırma */}
              <div className="my-10">
                <img
                  src="https://readdy.ai/api/search-image?query=Digital%20funnel%20sorting%20complex%20data%20streams%20by%20color%20into%20two%20separate%20glowing%20tubes%20labeled%20fast%20access%20and%20storage%2C%20modern%20tech%20illustration%20with%20blue%20gradient%20background%2C%20abstract%20data%20flow%20visualization%20concept%2C%20clean%20minimalist%20design%20showing%20data%20classification%20and%20organization&width=1200&height=600&seq=sentinel-data-classification&orientation=landscape"
                  alt="Veri Sınıflandırma Konsepti"
                  className="w-full h-auto rounded-2xl shadow-lg object-top"
                />
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center">
                <span className="w-10 h-10 flex items-center justify-center bg-[#0078D4] text-white rounded-lg mr-4 text-xl">4</span>
                Uzun Vadeli Saklama: Total Retention / Data Lake Storage
              </h2>

              <p>
                İnteraktif sorgular için veriyi sıcak tutmak pahalıdır. Varsayılan 90 günlük operasyonel süre (E5 lisans avantajlarıyla birleştiğinde) anlık tehdit avcılığı için genellikle yeterlidir. Ancak yasal regülasyonlar gereği yıllarca saklanması gereken denetim logları için Total Retention / Data Lake Storage stratejisi kullanılmalıdır.
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 my-8">
                <h4 className="font-bold text-gray-900 mb-4 text-lg">Total Retention / Data Lake Storage Avantajları:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-time-line text-amber-600 text-xl mr-3 mt-1"></i>
                    <span className="text-sm text-gray-700">Sentinel Data Lake ile veriyi 12 yıla kadar çok düşük GB maliyetleriyle saklayabilirsiniz.</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-search-line text-amber-600 text-xl mr-3 mt-1"></i>
                    <span className="text-sm text-gray-700">Gerektiğinde bu verilere ulaşmak için Search Job çalıştırabilir veya spesifik veri aralıklarını Restore işlemi ile geçici olarak sıcak katmana çekip tam kapasite KQL ile sorgulayabilirsiniz.</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-shield-check-line text-amber-600 text-xl mr-3 mt-1"></i>
                    <span className="text-sm text-gray-700">Uyumluluk gereksinimlerini karşılarken operasyonel maliyetleri minimize eder.</span>
                  </li>
                </ul>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center">
                <span className="w-10 h-10 flex items-center justify-center bg-[#0078D4] text-white rounded-lg mr-4 text-xl">5</span>
                Sentinel Data Lake vs. Azure Data Explorer (ADX)
              </h2>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg my-8">
                <h4 className="font-bold text-gray-900 mb-3 text-lg">Geçmişte ADX Kullanımı:</h4>
                <p className="text-base text-gray-700 mb-3">
                  Eskiden Sentinel'de veriyi ucuz yolla uzun süreli saklamanın tek yolu, veriyi bir "Logic App" veya "Data Export" ile harici bir ADX kümesine (cluster) taşımaktı.
                </p>
                <p className="text-base text-gray-700">
                  <strong>Dezavantajı:</strong> Ayrı bir kaynak yönetimi, ayrı bir maliyet kalemi ve veriyi sorgularken "cross-cluster" (farklı kümeler arası) sorgu yazma zorunluluğu vardı.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#0078D4] to-[#005A9E] text-white rounded-2xl p-8 my-8">
                <h3 className="text-2xl font-bold mb-6">Sentinel Data Lake: Yeni Nesil Çözüm</h3>
                <p className="text-white/95 mb-6">
                  Microsoft, Sentinel Data Lake (Auxiliary Logs altyapısı) ile ADX'in sunduğu düşük maliyetli saklama yeteneğini Sentinel'in içine gömdü.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg mr-4 flex-shrink-0 mt-1">
                      <i className="ri-check-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-bold mb-1">Entegrasyon:</h4>
                      <p className="text-white/90 text-sm">Artık veriyi dışarı çıkarmaya (export) gerek kalmadı. Her şey Sentinel içinde yönetiliyor.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg mr-4 flex-shrink-0 mt-1">
                      <i className="ri-money-dollar-circle-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-bold mb-1">Maliyet:</h4>
                      <p className="text-white/90 text-sm">Sentinel Data Lake fiyatlandırması, ADX kümesi işletme maliyetine (compute + storage) çok yaklaştı ve yönetim yükünü ortadan kaldırdı.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg mr-4 flex-shrink-0 mt-1">
                      <i className="ri-search-2-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-bold mb-1">Sorgulama:</h4>
                      <p className="text-white/90 text-sm">Data Lake'teki verileri sorgulamak artık çok daha kolay (KQL ile doğrudan). Sentinel arayüzünden çıkmadan tüm verilere erişebilirsiniz.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg mr-4 flex-shrink-0 mt-1">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-bold mb-1">Saklama Süresi:</h4>
                      <p className="text-white/90 text-sm">12 yıla kadar veri saklama imkanı sunar.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 my-8">
                <h4 className="font-bold text-gray-900 mb-4 text-lg flex items-center">
                  <i className="ri-question-line text-[#0078D4] text-2xl mr-3"></i>
                  ADX Hangi Durumlarda Hala Geçerli?
                </h4>
                <p className="text-base text-gray-700 mb-4">
                  Sentinel Data Lake'in sunduğu avantajlar nedeniyle ADX artık "birinci öncelik" olarak önerilmemektedir. Ancak şu özel durumlar için hala bir seçenek olabilir:
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg mr-4 flex-shrink-0 mt-1">
                      <i className="ri-database-2-line text-gray-600"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-1">Devasa Veri Boyutları (Petabyte seviyesi)</h5>
                      <p className="text-sm text-gray-600">Eğer on yıllarca sürecek petabyte'larca log varsa, ADX hala bir tık daha ekonomik olabilir.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg mr-4 flex-shrink-0 mt-1">
                      <i className="ri-bar-chart-box-line text-gray-600"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-1">Sentinel Dışı Analizler</h5>
                      <p className="text-sm text-gray-600">Veri sadece güvenlik için değil, aynı zamanda iş analitiği (BI) veya uygulama telemetrisi için kullanılıyorsa ADX mantıklı kalmaya devam eder.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg mr-4 flex-shrink-0 mt-1">
                      <i className="ri-git-merge-line text-gray-600"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-1">Merkezi Log Yönetimi</h5>
                      <p className="text-sm text-gray-600">Birden fazla Sentinel workspace'inden gelen verileri tek bir devasa havuzda toplamak istiyorsanız ADX hala bir seçenek.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#F0F6FF] border-l-4 border-[#0078D4] p-6 rounded-lg my-8">
                <h4 className="font-bold text-gray-900 mb-2 text-lg">Güncel Öneri:</h4>
                <p className="text-base text-gray-700">
                  Eskiden maliyet optimizasyonu için veriyi Azure Data Explorer'a (ADX) taşımak en popüler yöntemdi. Ancak günümüzde <strong>Microsoft Sentinel Data Lake (Auxiliary Tier)</strong> özelliği sayesinde, veriyi dışarı aktarmaya gerek kalmadan Sentinel içerisinde çok düşük maliyetlerle 12 yıla kadar saklayabiliyoruz. ADX, artık sadece çok özel ve devasa ölçekli merkezi log projeleri için bir alternatif olarak değerlendirilmelidir.
                </p>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
                Sonuç: Operasyonel Körlük Yaşamadan Optimize Edin
              </h2>

              <p>
                Maliyet optimizasyonu "bir kez yap ve unut" işi değildir. Sürekli bir döngüdür ve güvenlik görünürlüğünüzden ödün vermeden yapılmalıdır.
              </p>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 my-8">
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#0078D4] text-white rounded-lg mr-4 flex-shrink-0 font-bold text-sm">
                      1
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">İzleyin</h4>
                      <p className="text-sm text-gray-700">Hangi veri kaynağının en çok veri tükettiğini düzenli olarak görün. Usage and Estimated Costs raporlarını haftalık inceleyin.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#0078D4] text-white rounded-lg mr-4 flex-shrink-0 font-bold text-sm">
                      2
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Sınıflandırın</h4>
                      <p className="text-sm text-gray-700">Bu veri gerçekten yüksek analitik mi olmalı, yoksa Data Lake Tier seviyesi mi? DCR kurallarınızı gözden geçirin ve Basic Logs kullanımlarınızı Data Lake Tier'a geçirmeyi planlayın.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#0078D4] text-white rounded-lg mr-4 flex-shrink-0 font-bold text-sm">
                      3
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Uzun Vadeli Saklama</h4>
                      <p className="text-sm text-gray-700">Belirli bir süreden eski veriyi Total Retention / Data Lake Storage stratejisi ile yönetin. Sentinel Data Lake 12 yıla kadar saklama imkanı sunar.</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-[#F0F6FF] border-l-4 border-[#0078D4] p-6 rounded-lg my-8">
                <p className="text-base font-semibold text-gray-900">
                  Güvenlik bütçenizi lisanslara değil, insan kaynağına ve ileri düzey tehdit avcılığına (Threat Hunting) harcayın. Unutmayın, en güvenli sistem, sürdürülebilir olandır.
                </p>
              </div>
            </div>
          </motion.article>
        </div>
      </section>

      {/* Alt Navigasyon */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-8">
          <div className="flex items-center justify-between">
            <Link
              to="/sentinel"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all cursor-pointer text-gray-700 hover:text-[#0078D4]"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              <span className="text-sm font-medium whitespace-nowrap">Sentinel Sayfası</span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all cursor-pointer text-gray-700 hover:text-[#0078D4]"
            >
              <span className="text-sm font-medium whitespace-nowrap">Ana Sayfa</span>
              <i className="ri-arrow-right-line text-lg"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SentinelMaliyetOptimizasyonuPage;
